import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import EmailComposer from './EmailComposer';

/**
 * ReplyComposer - Wrapper for reply, reply all, and forward
 * Handles recipient and subject pre-population based on mode
 */
const ReplyComposer = ({
  message,
  mode = 'reply', // 'reply' | 'replyAll' | 'forward'
  userEmail = '',
  onSend,
  onClose,
  sending = false,
}) => {
  // Calculate initial recipients based on mode
  const initialRecipients = useMemo(() => {
    if (!message) return { to: [], cc: [], bcc: [] };

    const currentUserEmail = userEmail.toLowerCase();
    const senderEmail = message.sender?.email?.toLowerCase() || '';

    switch (mode) {
      case 'reply': {
        // Reply to sender only (or first recipient if replying to own email)
        const to = [];
        if (senderEmail && senderEmail !== currentUserEmail) {
          to.push(message.sender.email);
        } else if (message.to?.[0]?.email) {
          to.push(message.to[0].email);
        }
        return { to, cc: [], bcc: [] };
      }

      case 'replyAll': {
        const to = [];
        const cc = [];

        // Add sender if not self
        if (senderEmail && senderEmail !== currentUserEmail) {
          to.push(message.sender.email);
        }

        // Add original To recipients (except self and sender)
        (message.to || []).forEach((r) => {
          const email = r.email?.toLowerCase();
          if (email && email !== currentUserEmail && email !== senderEmail) {
            to.push(r.email);
          }
        });

        // Add original CC recipients (except self)
        (message.cc || []).forEach((r) => {
          const email = r.email?.toLowerCase();
          if (email && email !== currentUserEmail && !to.includes(r.email)) {
            cc.push(r.email);
          }
        });

        return { to, cc, bcc: [] };
      }

      case 'forward':
        return { to: [], cc: [], bcc: [] };

      default:
        return { to: [], cc: [], bcc: [] };
    }
  }, [message, mode, userEmail]);

  // Calculate initial subject
  const initialSubject = useMemo(() => {
    if (!message?.subject) return '';

    const subject = message.subject;
    const lowerSubject = subject.toLowerCase();

    switch (mode) {
      case 'reply':
      case 'replyAll':
        return lowerSubject.startsWith('re:') ? subject : `Re: ${subject}`;
      case 'forward':
        return lowerSubject.startsWith('fwd:') ? subject : `Fwd: ${subject}`;
      default:
        return subject;
    }
  }, [message?.subject, mode]);

  // Calculate initial message body (for forward)
  const initialMessage = useMemo(() => {
    if (mode !== 'forward' || !message) return '';

    const date = message.receivedOn
      ? new Date(message.receivedOn).toLocaleString()
      : '';

    const toList = (message.to || []).map((r) => r.email).join(', ');

    return `
<br/><br/>
<div style="border-left: 2px solid #ccc; padding-left: 12px; margin-left: 0; color: #555;">
  <p style="margin: 0 0 10px 0; font-size: 13px;">
    ---------- Forwarded message ----------<br/>
    <strong>From:</strong> ${message.sender?.name || ''} &lt;${message.sender?.email || ''}&gt;<br/>
    <strong>Date:</strong> ${date}<br/>
    <strong>Subject:</strong> ${message.subject || ''}<br/>
    <strong>To:</strong> ${toList}
  </p>
  <div style="margin-top: 10px;">
    ${message.decodedBody || message.body || ''}
  </div>
</div>
    `.trim();
  }, [message, mode]);

  // Handle send with threading headers
  const handleSend = async (data) => {
    if (!onSend) return;

    const emailData = {
      ...data,
      headers: {},
    };

    // Add threading headers for reply
    if (mode === 'reply' || mode === 'replyAll') {
      if (message?.id) {
        emailData.headers['In-Reply-To'] = message.id;
        emailData.headers['References'] = message.id;
      }
      if (message?.threadId) {
        emailData.threadId = message.threadId;
      }
    }

    await onSend(emailData);
  };

  if (!message) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <EmailComposer
        initialTo={initialRecipients.to}
        initialCc={initialRecipients.cc}
        initialBcc={initialRecipients.bcc}
        initialSubject={initialSubject}
        initialMessage={initialMessage}
        onSend={handleSend}
        onClose={onClose}
        sending={sending}
        mode={mode}
        compact
        autoFocus
        replyingTo={message.sender?.email}
      />
    </Box>
  );
};

ReplyComposer.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string,
    threadId: PropTypes.string,
    subject: PropTypes.string,
    sender: PropTypes.shape({
      email: PropTypes.string,
      name: PropTypes.string,
    }),
    to: PropTypes.arrayOf(
      PropTypes.shape({
        email: PropTypes.string,
        name: PropTypes.string,
      })
    ),
    cc: PropTypes.arrayOf(
      PropTypes.shape({
        email: PropTypes.string,
        name: PropTypes.string,
      })
    ),
    receivedOn: PropTypes.string,
    body: PropTypes.string,
    decodedBody: PropTypes.string,
  }),
  mode: PropTypes.oneOf(['reply', 'replyAll', 'forward']),
  userEmail: PropTypes.string,
  onSend: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  sending: PropTypes.bool,
};

export default ReplyComposer;
